import React, { Dispatch, useMemo, useState } from 'react';
import ListingsMap, { Neighborhood } from '../../ListingsMap';
import PostcodeMap from '../../PostcodeMap';
import { GeoArea, Location, PostCode } from '../../services/listingsSearchCriteriaApi';
import { isEffectivelySelected, PostcodeIndex, togglePostcode } from '../../postcodeUtils';
import { WizardAction, WizardState } from './wizardState';

type Props = {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
  locations: Location[];
  postcodes: PostCode[];
  postcodeIndex: PostcodeIndex;
};

export default function WhereStep(props: Props) {
  return props.state.mode === 'postcode' ? <PostcodeWhere {...props} /> : <NeighbourhoodWhere {...props} />;
}

function SelectedChips({ items, onRemove, onClear }: {
  items: { id: string; label: string }[];
  onRemove: (id: string) => void;
  onClear: () => void;
}) {
  if (!items.length) return null;
  return (
    <div>
      <div className="ma-row">
        <span className="ma-section-title">{items.length} selected</span>
        <button type="button" className="ma-link-button" onClick={onClear}>Clear</button>
      </div>
      <div className="ma-chips">
        {items.map(item => (
          <button
            key={item.id}
            type="button"
            className="ma-chip ma-chip--removable"
            aria-label={`Remove ${item.label}`}
            onClick={() => onRemove(item.id)}
          >
            {item.label}<span aria-hidden>✕</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NeighbourhoodWhere({ state, dispatch, locations }: Props) {
  const [query, setQuery] = useState('');
  const nameById = useMemo(() => new Map(locations.map(location => [location.id, location.name])), [locations]);
  const neighborhoods: Neighborhood[] = useMemo(
    () => locations
      .filter(location => !!location.geoArea)
      .map(location => ({ id: location.id, name: location.name, geoArea: location.geoArea as GeoArea })),
    [locations],
  );

  const normalizedQuery = query.trim().toLowerCase();
  const suggestions = normalizedQuery
    ? locations
      .filter(location => !state.areas.includes(location.id) && location.name.toLowerCase().includes(normalizedQuery))
      .slice(0, 6)
    : [];

  return (
    <div className="ma-stack">
      <div>
        <h1 className="ma-title">Where are you looking to rent?</h1>
        <p className="ma-subtitle">Search, or tap neighbourhoods on the map.</p>
        <input
          className="ma-input"
          placeholder="Search neighbourhoods"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {normalizedQuery && (
          <div className="ma-suggest">
            {suggestions.map(location => (
              <button
                key={location.id}
                type="button"
                className="ma-suggest__item"
                onClick={() => {
                  dispatch({ type: 'toggleArea', id: location.id });
                  setQuery('');
                }}
              >
                {location.name}
              </button>
            ))}
            {!suggestions.length && (
              <p className="ma-hint" style={{ padding: '0 14px 12px' }}>No match. Try a nearby area or search by postcode.</p>
            )}
          </div>
        )}
      </div>
      <SelectedChips
        items={state.areas.map(id => ({ id, label: nameById.get(id) ?? id }))}
        onRemove={id => dispatch({ type: 'toggleArea', id })}
        onClear={() => dispatch({ type: 'setAreas', ids: [] })}
      />
      {neighborhoods.length > 0 && (
        <div className="ma-map">
          <ListingsMap
            neighborhoods={neighborhoods}
            selectedIds={state.areas}
            onToggle={id => dispatch({ type: 'toggleArea', id })}
          />
        </div>
      )}
      <p className="ma-hint">Tap a neighbourhood to add it. Drag to move.</p>
    </div>
  );
}

function PostcodeWhere({ state, dispatch, postcodes, postcodeIndex }: Props) {
  const [query, setQuery] = useState('');
  const selection = useMemo(() => ({ selectedCodes: new Set(state.postcodes) }), [state.postcodes]);
  const toggle = (code: string) => {
    const next = togglePostcode(code, postcodeIndex, selection);
    dispatch({ type: 'setPostcodes', codes: Array.from(next.selectedCodes) });
  };

  const normalizedQuery = query.toUpperCase().replace(/\s+/g, '');
  const districtMatches = normalizedQuery
    ? postcodeIndex.districts.filter(district => district.code.startsWith(normalizedQuery)).slice(0, 15)
    : [];
  const area = /^[A-Z]+$/.test(normalizedQuery) ? postcodeIndex.byCode.get(normalizedQuery) : undefined;
  const areaDistricts = area ? postcodeIndex.districts.filter(district => district.parent === area.code) : [];
  const areaSelected = area ? isEffectivelySelected(area.code, postcodeIndex, selection) : false;

  return (
    <div className="ma-stack">
      <div>
        <h1 className="ma-title">Which postcodes?</h1>
        <p className="ma-subtitle">Type a postcode or tap districts on the map.</p>
        <input
          className="ma-input"
          placeholder="Search postcodes"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {normalizedQuery && (
          <div className="ma-chips" style={{ marginTop: 10 }}>
            {area && areaDistricts.length > 1 && (
              <button type="button" className="ma-chip" onClick={() => { toggle(area.code); setQuery(''); }}>
                {areaSelected ? 'Remove all' : 'Add all'} {area.code} districts ({areaDistricts.length})
              </button>
            )}
            {districtMatches.map(district => (
              <button
                key={district.code}
                type="button"
                className="ma-chip"
                aria-pressed={isEffectivelySelected(district.code, postcodeIndex, selection)}
                onClick={() => toggle(district.code)}
              >
                {district.code}
              </button>
            ))}
            {!districtMatches.length && !area && <p className="ma-hint">No London district starts with that.</p>}
          </div>
        )}
      </div>
      <SelectedChips
        items={state.postcodes.map(code => ({ id: code, label: code }))}
        onRemove={toggle}
        onClear={() => dispatch({ type: 'setPostcodes', codes: [] })}
      />
      {postcodes.length > 0 && (
        <div className="ma-map">
          <PostcodeMap postcodes={postcodes} selectionState={selection} onToggle={toggle} />
        </div>
      )}
      <p className="ma-hint">Tap districts to add or remove. Drag to move.</p>
    </div>
  );
}
